import React, { useState, useEffect } from "react"
import DataTable from "aldoo-ra/DataTable"
import Typography from "aldoo-ra/Typography"
import Checkbox from "aldoo-ra/Checkbox"
import Button from "aldoo-ra/Button"
import { showNotification } from "aldoo-ra/Notification"
import { showPromptDialog } from "aldoo-ra/PromptDialog"
import { GetUserDevices, DeactivateDevice } from "aldoo-ra/Admin/ProductLicensing/api"
import Localization from "aldoo-ra/Localization"
import TextBundle from "aldoo-ra/CMS/text-bundle"
import { TrashIcon } from "@heroicons/react/24/outline"
import { getPlatformIcon } from "aldoo-ra/Admin/ProductLicensing/PlatformIcons"
import useSigninController from "aldoo-ra/Signin/signin-controller"

const Devices = () => {
  const { userProfile } = useSigninController()
  const [selectedDevices, setSelectedDevices] = useState([])
  const [selectedDeviceData, setSelectedDeviceData] = useState([])
  const [refreshKey, setRefreshKey] = useState(0)
  const [isMobile, setIsMobile] = useState(false)
  const { t } = Localization()
  const texts = TextBundle({ id: "account_devices" })

  // Check if mobile on mount and resize
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768)
    }
    
    checkMobile()
    window.addEventListener('resize', checkMobile)
    
    return () => window.removeEventListener('resize', checkMobile)
  }, [])

  // Handle bulk device removal
  const handleBulkRemoveDevices = () => {
    if (selectedDevices.length === 0) return

    const deviceCount = selectedDevices.length
    const deviceText = deviceCount === 1 ? texts?.devices_singular : texts?.devices_plural

    showPromptDialog({
      title: t(texts?.remove_dialog_title, {deviceCount, deviceText}),
      //message: `Are you sure you want to remove ${deviceCount} ${deviceText} from your licenses? This action cannot be undone.`,
      message: t(texts?.remove_dialog_message, {deviceCount, deviceText}),
      confirmText: texts?.remove_dialog_confirm_text || "Remove",
      confirmVariant: "warning",
      onConfirm: async () => {
        try {
          // Deactivate each device individually using stored device data
          const promises = selectedDeviceData.map(async (device) => {
            return DeactivateDevice({
              licenseId: device.primaryLicense?.id,
              deviceId: device.deviceID || device._id || device.deviceId,
            })
          })

          const results = await Promise.allSettled(promises)
          const successful = results.filter(r => r.status === 'fulfilled' && r.value.result)
          
          if (successful.length > 0) {
            showNotification({
              message: t(texts?.removal_success_message, {
                deviceCount: successful.length,
                deviceText: successful.length === 1 ? texts?.devices_singular : texts?.devices_plural
              }),
              className: "bg-success text-white",
            })
            setSelectedDevices([])
            setSelectedDeviceData([])
            setRefreshKey(prev => prev + 1)
          }

          if (successful.length < deviceCount) {
            const failedCount = deviceCount - successful.length
            showNotification({
               message: t(texts?.removal_failed_message, {
                deviceCount: failedCount,
                deviceText: failedCount === 1 ? texts?.devices_singular : texts?.devices_plural
              }),
              className: "bg-error text-white",
            })
          }
        } catch (error) {
          console.error('Bulk device removal error:', error)
          showNotification({
            message: texts?.bulk_removal_error_message || "Error during bulk device removal",
            className: "bg-error text-white",
          })
        }
      },
    })
  }

  const requestFields = {
    match: { platform: { $in: ['iOS', 'Android'] } }
  }

  // Table footer content - action buttons
  const tableFooter = selectedDevices.length > 0 && (
    <div className="flex justify-end items-center p-2">
      <Typography 
        variant="info_18" 
        className="text-text_secondary mr-4 my-0"
      >
        {t(texts?.devices_selected_message, {
          deviceCount: selectedDevices.length,
          deviceText: selectedDevices.length === 1 ? texts?.devices_singular : texts?.devices_plural
        })}
      </Typography>


      <Button
        variant="secondary_s"
        className="mr-3"
        onClick={() => {
          setSelectedDevices([])
          setSelectedDeviceData([])
        }}
      >
        {texts?.deselect_btn || "Deselect"}
      </Button>

      <Button 
        variant="secondary_s" 
        className="text-error border-error hover:bg-error hover:text-white"
        onClick={() => handleBulkRemoveDevices()}
      >
        <TrashIcon className="w-4 h-4 mr-2" />
        {texts?.remove_btn || "Remove"}
      </Button>
    </div>
  )


  return (
    <div className="space-y-0">
      {/* Devices DataTable */}
      <div className="w-full max-w-[554px] lg:max-w-full bg-paper rounded-[24px] shadow-custom_card px-4 xl:px-6 py-4 xl:py-6 mx-auto lg:mx-0">
        <DataTable
          variant="browser"
          apiMethod={GetUserDevices}
          requestFields={requestFields}
          refreshKey={refreshKey}
          searchField="name model platform"
          tableFooter={tableFooter}
          itemsPerPage={10}
          localUserPreferencesKey="account.devices"
          onDataLoadingElement={
            <div className="flex items-center justify-center py-20">
              <div className="text-text_secondary">Loading devices...</div>
            </div>
          }
          styleOverrides={{
            container: "relative w-full flex flex-col h-[70vh] justify-start bg-paper pt-6",
            searchInput: "absolute top-2 right-1/2 md:right-0 translate-x-1/2 md:translate-x-0  text-text bg-transparent outline-none flex-1 w-[250px] md:w-[350px] h-[30px] px-2 py-1",
            getRowStyle: (index, item) => {
              const isSelected = selectedDevices.includes(item.deviceID || item._id || item.deviceId)
              let style = "bg-paper border-b border-neutral_1"
              
              if (isSelected) {
                style = "bg-primary/10 border-b border-neutral_1"
              }
              
              style += " hover:bg-primary_lt transition-colors duration-200 cursor-pointer"
              return style
            },
            onRowClick: (item) => {
              const deviceId = item.deviceID || item._id || item.deviceId
              const isSelected = selectedDevices.includes(deviceId)
              if (isSelected) {
                setSelectedDevices((prev) => prev.filter((id) => id !== deviceId))
                setSelectedDeviceData((prev) => prev.filter((device) => (device.deviceID || device._id || device.deviceId) !== deviceId))
              } else {
                setSelectedDevices((prev) => [...prev, deviceId])
                setSelectedDeviceData((prev) => [...prev, item])
              }
            }
          }}
          renders={[
            ...(!isMobile ? [{
              id: "select",
              headerClassName: "w-10",
              header: (_, items) => (
                <Checkbox
                  isOn={items.length > 0 && selectedDevices.length === items.length}
                  onChange={(isSelected) => {
                    if (isSelected) {
                      setSelectedDevices(items.map((item) => item.deviceID || item._id || item.deviceId))
                      setSelectedDeviceData(items)
                    } else {
                      setSelectedDevices([])
                      setSelectedDeviceData([])
                    }
                  }}
                />
              ),
              render: (_, item) => (
                <div onClick={(e) => e.stopPropagation()}>
                  <Checkbox
                    isOn={selectedDevices.includes(item.deviceID || item._id || item.deviceId)}
                    onChange={(isSelected) => {
                      const deviceId = item.deviceID || item._id || item.deviceId
                      if (isSelected) {
                        setSelectedDevices((prev) => [...prev, deviceId])
                        setSelectedDeviceData((prev) => [...prev, item])
                      } else {
                        setSelectedDevices((prev) => prev.filter((id) => id !== deviceId))
                        setSelectedDeviceData((prev) => prev.filter((device) => (device.deviceID || device._id || device.deviceId) !== deviceId))
                      }
                    }}
                  />
                </div>
              ),
            }] : []),
            {
              id: "device",
              title: texts?.device_column || "Device",
              headerClassName: `${isMobile ? 'w-[70%]' : 'w-[60%] md:w-[80%]'}`,
              render: (_, device) => (
                <div className="flex items-center space-x-3">
                  {getPlatformIcon(device.platform, "w-8 h-8 text-text_secondary")}

                  <div>
                    <Typography 
                      variant="info_16_accent" 
                      className="font-semibold my-0"
                    >
                      {device.name || "Unnamed Device"}
                    </Typography>

                    {device.model && (
                      <Typography 
                        variant="info_14" 
                        className="text-text_secondary my-0"
                      >
                        {device.model}
                      </Typography>
                    )}
                  </div>
                </div>
              ),
            },
            {
              id: "created",
              title: texts?.created_column || "Created",
              render: (_, device) => (
                <Typography 
                  variant="info_14"  
                  className="text-text my-0"
                >
                  {device.created 
                    ? new Date(device.created).toLocaleDateString()
                    : texts?.unknown || "Unknown"
                  }
                </Typography>
              ),
            },
          ]}
        />
      </div>
    </div>
  )
}

export default Devices